// Modal to display a suggestion from the database and allow the user to add it to the database, edit it, or delete it.

import React, { useState } from 'react';
import { Modal, ModalHeader, ModalDescription, ModalContent, ModalActions, Button, Header, Image, Input, TextArea } from 'semantic-ui-react';
import AutoSVG from "../../visualizer-tool/automatic-svg/AutoSVG";
import ConfirmationModal from './ConfirmationModal';
import { formatString } from '../../visualizer-tool/glycan-interpreter/parser';

const SuggestionModal = ({ open, onClose, entry, onDelete, onEdit, onAdd }) => {
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [action, setAction] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editableEntry, setEditableEntry] = useState({ ...entry });

    // handle the confirmation modal based on the action, then close the modal
    const handleConfirm = () => {
        if (action === 'delete') {
            onDelete(entry._id);
        } else if (action === 'add') {
            onAdd(entry._id);
        } else if (action === 'edit') {
            saveEdit();
        }
        setConfirmationOpen(false);
    };

    // open the confirmation modal and set the action
    const openConfirmationModal = (action) => {
        console.log('Opening confirmation modal for', action);
        setAction(action);
        setConfirmationOpen(true);
    };

    // save the edited entry, automatically format the antigen structure for the carbuilder fomrat, then pass it to the onEdit callback
    const saveEdit = () => {
        console.log('Saving edit');

        // save carbuilder format of the antigen structure
        editableEntry.Carbuilder_structure_format = formatString(editableEntry.Antigen_structure);

        onEdit(editableEntry); // Pass the edited entry to the onEdit callback
        setEditMode(false);
    };

    // handle input change as user types and update the editable entry
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target; // Destructure properties from e.target

        // Handle input changes based on type
        if (type === 'radio') {
            setEditableEntry({ ...editableEntry, [name]: checked ? parseInt(value, 10) : editableEntry[name] });
        } else {
            // Handle other input types
            let newValue = value;
            if (name === 'Antigen_structure') {
                newValue = newValue.replace(/->/g, '→').replace(/\\a/g, 'α').replace(/\\b/g, 'β');
            }
            setEditableEntry({ ...editableEntry, [name]: newValue });
        }
    };

    // return the editable entry to its original state
    const resetEditableEntry = () => {
        setEditableEntry({ ...entry });
        setEditMode(false);
    }

    return (
        <> 
        <Modal closeIcon open={open} onClose={onClose}>
            <ModalHeader>Would you like to add {entry.Name}'s suggestion to the database?</ModalHeader>
            <ModalContent scrolling>
            <Image size='large' wrapped><AutoSVG casperString={entry.Antigen_structure} /></Image>
            <ModalDescription>
                <Header>Suggestion details: </Header>
                <p><strong>Serotype: </strong> 
                    {editMode ? 
                        <Input fluid name="Serotype" value={editableEntry.Serotype} onChange={handleInputChange} /> : 
                        editableEntry.Serotype}
                </p>
                <p><strong>Antigen structure (in CASPER format):</strong> 
                    {editMode ? 
                        <Input fluid name="Antigen_structure" value={editableEntry.Antigen_structure} onChange={handleInputChange} /> : 
                        editableEntry.Antigen_structure}
                </p>
                <p><strong>NMR:</strong> 
                    {editMode ? 
                        <Input fluid name="NMR" value={editableEntry.NMR} onChange={handleInputChange} /> : 
                        editableEntry.NMR}
                </p>
                <p><strong>Species where found:</strong> 
                    {editMode ? 
                        <Input fluid name="Species_where_found" value={editableEntry.Species_where_found} onChange={handleInputChange} /> : 
                        editableEntry.Species_where_found}
                </p>
                <p><strong>Antigen type:</strong> 
                    {editMode ? 
                        <Input fluid name="Antigen_type" value={editableEntry.Antigen_type} onChange={handleInputChange} /> : 
                        editableEntry.Antigen_type}
                </p>
                <p><strong>Molecular type:</strong> 
                    {editMode ? 
                        <Input fluid name="Molecular_type" value={editableEntry.Molecular_type} onChange={handleInputChange} /> : 
                        editableEntry.Molecular_type}
                </p>
                <p><strong>DOI:</strong> 
                    {editMode ? 
                        <Input fluid name="DOI" value={editableEntry.DOI} onChange={handleInputChange} /> : 
                        editableEntry.DOI}
                </p>
                <p><strong>References:</strong> 
                    {editMode ? 
                        <Input fluid name="References" value={editableEntry.References} onChange={handleInputChange} /> : 
                        editableEntry.References}
                </p>
                <p><strong>Branched:</strong> 
                    {editMode ? 
                        <>                        
                        <div className="ui radio checkbox">
                            <input type="radio" name="Branched" value='1' checked={editableEntry.Branched === 1} onChange={handleInputChange} />
                            <label>Yes</label>
                        </div>
                        <div className="ui radio checkbox">
                            <input type="radio" name="Branched" value='0' checked={editableEntry.Branched === 0} onChange={handleInputChange} />
                            <label>No</label>
                        </div>
                        </>
                        : 
                        (editableEntry.Branched)}
                </p>
                <p><strong>Repeat unit size:</strong> 
                    {editMode ? 
                        <Input fluid name="Repeat_unit_size" type='number' value={editableEntry.Repeat_unit_size} onChange={handleInputChange} /> : 
                        editableEntry.Repeat_unit_size}
                </p>
                <p><strong>Entry date:</strong> 
                    {editableEntry.EntryDate}
                </p>
            </ModalDescription>
            </ModalContent>
            <ModalActions>
                { !editMode ?
                <>
                <Button color='black' icon='edit' content='Edit' onClick={() => setEditMode(true)}/>
                <Button negative icon='trash alternate' content='Delete suggestion' onClick={() => openConfirmationModal('delete')} />
                <Button positive icon='database' content='Add suggestion to database' onClick={() => openConfirmationModal('add')}/>
                </>
                : 
                <>
                <Button positive icon='check' content='Save edit' onClick={() => openConfirmationModal('edit')}/>
                <Button negative icon='cancel' content='Reset edit' onClick={() => resetEditableEntry()}/>
                </>
                }
            </ModalActions>
        </Modal>

        <ConfirmationModal
            open={confirmationOpen}
            onClose={() => setConfirmationOpen(false)}
            onConfirm={handleConfirm}
            message={`Are you sure you want to ${action} this suggestion?`}
        />
        </>
    );
}

export default SuggestionModal;
